import React, { useEffect, useRef, useState } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { useMutation, useApolloClient } from '@apollo/client';
import { Card } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { NavLinkTab } from 'components/navigation/nav-link-tab';
import { useAccountContext } from 'contexts/account';
import { CarouselButtons } from 'components/carousel-buttons';
import { useCurrentPastNotifications } from 'hooks/use-current-past-notification.hook';
import { totalCurrentNotificationsVar } from 'graphql/cache';
import { priorityTypes } from 'domain/notifications';
import { MARK_AS_READ } from './current-past.api';
import { Current } from './current';
import { Past } from './past';
import { Header } from './current-past-module.style';

const getCopy = copyFor('notifications.currentPastModule');
const routeTemplates = routes();

export const CurrentPastModule = () => {
  const client = useApolloClient();
  const { customerType } = useAccountContext();
  const isSMB = customerType === 'smb';
  const allNotificationIdsRef = useRef(new Set<number>());
  const upToDateIds = useRef<number[]>([]);
  const [tab, setTab] = useState('current-tab');
  const [page, setPage] = useState(0);
  const [length, setLength] = useState(1);
  const highPriorityUnreadCountRef = useRef(0);

  const { data, error, loading } = useCurrentPastNotifications({
    tag: tab,
    currentTabPage: page + 1,
    currentTabPageSize: 10,
    pastTabPage: page + 1,
    pastTabPageSize: 10,
  });

  const { selectedAccountId } = useAccountContext();
  const cacheId = `Account:${selectedAccountId}`;

  const [mutate] = useMutation(MARK_AS_READ);

  const {
    currentNotifications,
    pastNotifications,
    totalCurrentNotifications,
    totalPastNotifications,
  } = data?.account?.dashboardNotifications ?? {
    currentNotifications: [],
    pastNotifications: [],
    totalCurrentNotifications: undefined,
    totalPastNotifications: undefined,
  };

  const linkPadding = '24px 8px 16px';

  useEffect(() => {
    if (currentNotifications?.length) {
      let highPriorityUnreadCount = 0;

      currentNotifications.forEach(({ id, read, priorityType }) => {
        if (!read) {
          allNotificationIdsRef.current.add(id);

          if (
            priorityType === priorityTypes.highError ||
            priorityType === priorityTypes.highNonError
          ) {
            highPriorityUnreadCount++;
          }
        }
      });
      upToDateIds.current = Array.from(allNotificationIdsRef.current);
      highPriorityUnreadCountRef.current = highPriorityUnreadCount;
    }
  }, [currentNotifications]);

  useEffect(() => {
    return () => {
      const idsToMarkRead = upToDateIds.current;
      if (idsToMarkRead.length) {
        mutate({
          variables: {
            input: {
              ids: idsToMarkRead,
              read: true,
            },
          },
          onCompleted: () => {
            const currentCount = totalCurrentNotificationsVar();

            const newCount = Math.max(
              currentCount -
                (idsToMarkRead.length - highPriorityUnreadCountRef.current),
              0
            );
            totalCurrentNotificationsVar(newCount);

            client.cache.modify({
              id: cacheId,
              fields: {
                'dashboardNotifications({"input":{"tag":"dashboard"}})':
                  function (existingData: DashboardNotifications) {
                    if (!existingData) return existingData;

                    return {
                      ...existingData,
                      totalCurrentNotifications: newCount,
                    };
                  },
              },
            });
          },
        });
      }
    };
  }, [mutate]);

  useEffect(() => {
    setLength(prevLength => {
      if (tab === 'current-tab' && totalCurrentNotifications !== undefined) {
        return Math.ceil(totalCurrentNotifications / 10) || prevLength;
      } else if (tab === 'past-tab' && totalPastNotifications !== undefined) {
        return Math.ceil(totalPastNotifications / 10) || prevLength;
      }
      return prevLength;
    });
  }, [tab, totalCurrentNotifications, totalPastNotifications]);

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handleTabChange = newTab => {
    setTab(newTab);
    setPage(0);
    setLength(1);
  };

  return (
    <Card mobileVariant="flat">
      <Header>
        <NavLinkTab
          padding={linkPadding}
          path={routeTemplates.currentNotifications}
          copy={getCopy('links.current')}
          margin={{ right: '24px' }}
          onClick={() => handleTabChange('current-tab')}
        />
        <NavLinkTab
          padding={linkPadding}
          path={routeTemplates.pastNotifications}
          copy={getCopy('links.past')}
          onClick={() => handleTabChange('past-tab')}
        />
        <CarouselButtons
          index={page}
          length={length}
          itemName="notification"
          setIndex={handlePageChange}
          justifyContent="flex-end"
        />
      </Header>
      <Switch>
        <Route
          path={routeTemplates.pastNotifications}
          render={() => (
            <Past
              notifications={pastNotifications}
              error={error}
              loading={loading}
              isSMB={isSMB}
            />
          )}
        />
        <Route
          path={routeTemplates.currentNotifications}
          render={() => (
            <Current
              notifications={currentNotifications}
              error={error}
              loading={loading}
              isSMB={isSMB}
            />
          )}
        />
        <Redirect to={routeTemplates.currentNotifications} />
      </Switch>
    </Card>
  );
};
